import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL_TIGHT } from "@/lib/brand";

// ─────────────────────────────────────────────────────────────────
// Prototype A — "CrewZNatioN Arrival"
//
// Experimental first-screen composition. Not part of the approved
// bilingual website. Exists solely to test a design philosophy:
// whether authentic CrewZNatioN photography can serve as the
// navigational language of the homepage, with Arabic and English
// acting only as supporting labels.
//
// Governance notes:
//  • Lives on a separate route so the approved English and Arabic
//    homepages remain fully preserved (PRD §41, §43.1).
//  • Uses only authentic CrewZNatioN photography already resident
//    in the project archive (Gathering.jsx source).
//  • No stock photography, no icons, no illustrations.
//  • Nothing on this route is locked. Editorial and image
//    assignments are provisional and open to full user revision.
// ─────────────────────────────────────────────────────────────────

// Provisional image → destination mappings using authentic
// photography from the Gathering archive. These are working
// selections for evaluation only — every assignment is open for
// the user to reassign.
const ARRIVAL_CARDS = [
    {
        id: "gathering",
        // Iconic Gathering moment — cultural pride, festival lights.
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        labelEn: "The Gathering",
        labelAr: null, // English institutional asset, retained per PRD §34.2
        to: "/gathering",
        variant: "lead",
    },
    {
        id: "crewznation",
        // Community — "Inside the meet — Riyadh comes together"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
        labelEn: "CrewZNatioN",
        labelAr: null,
        to: "/",
        variant: "quad",
    },
    {
        id: "events",
        // Convoy / active meet — Corvette trio under the festival lights
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
        labelEn: "Events",
        labelAr: "الفعاليات",
        to: "/events",
        variant: "quad",
    },
    {
        id: "moments",
        // Character shot — "1958 in full bloom — purple lowrider with roses"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
        labelEn: "Moments",
        labelAr: "اللحظات",
        to: "/gallery",
        variant: "quad",
    },
    {
        id: "welcome",
        // Invitation — "Crimson taillights in the night"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/g5uc2mab_8D0AD18F-55F9-4A03-89B6-01C85BCDB62B.JPG",
        labelEn: "Welcome",
        labelAr: "حياك",
        to: "/contact",
        variant: "quad",
    },
];

// A single arrival card — full-bleed authentic photograph with a
// quiet bilingual label anchored at the bottom-left. No icon, no
// arrow, no chip, no CTA. The photograph carries the meaning; the
// label is subordinate.
function ArrivalCard({ card }) {
    return (
        <Link
            to={card.to}
            className="group relative block w-full h-full overflow-hidden bg-[var(--crewz-bg-2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--crewz-gold)]"
            data-testid={`arrival-card-${card.id}`}
        >
            <div className="absolute inset-0 overflow-hidden">
                <img
                    src={card.image}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.045]"
                    draggable={false}
                />
                {/* Deepening gradient anchored to the label area — legibility
                    without an opaque overlay across the photograph. */}
                <div
                    className="absolute inset-0 pointer-events-none transition-opacity duration-700"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 28%, rgba(0,0,0,0.08) 55%, transparent 85%)",
                    }}
                />
            </div>

            <div className="relative h-full w-full flex flex-col justify-end p-6 md:p-8">
                <div className="flex flex-col items-start gap-1.5">
                    {card.labelAr && (
                        <span
                            dir="rtl"
                            className="font-arabic text-[15px] md:text-[17px] font-bold text-white/95 leading-none"
                        >
                            {card.labelAr}
                        </span>
                    )}
                    <span
                        dir="ltr"
                        className="font-display text-lg md:text-xl text-white leading-none tracking-[0.005em]"
                    >
                        {card.labelEn}
                    </span>
                    {/* Quiet gold accent — reveals on hover, expresses
                        recognition rather than instruction. No arrows. */}
                    <span
                        className="mt-2 h-px w-6 bg-[var(--crewz-gold)]/0 group-hover:bg-[var(--crewz-gold)] group-hover:w-10 transition-all duration-500 ease-out"
                        aria-hidden="true"
                    />
                </div>
            </div>
        </Link>
    );
}

// A small, unobtrusive top strip carrying only the wordmark and a
// language toggle. No primary navigation — the photography IS the
// navigation.
function ArrivalTopStrip() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr
        ? "/prototypes/arrival"
        : "/prototypes/arrival?lang=ar";

    return (
        <div
            className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 md:px-10 lg:px-16 h-20"
            data-testid="arrival-top-strip"
        >
            <Link to="/" className="inline-flex items-center" data-testid="arrival-wordmark">
                <img
                    src={LOGO_URL_TIGHT}
                    alt="CrewZNatioN"
                    className="h-10 md:h-12 w-auto select-none opacity-90 hover:opacity-100 transition-opacity duration-300"
                    draggable={false}
                />
            </Link>
            <Link
                to={toggleTo}
                className={
                    isAr
                        ? "font-body uppercase tracking-[0.2em] text-[12px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                        : "font-arabic normal-case tracking-normal text-[14px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                }
                dir={isAr ? "ltr" : "rtl"}
                data-testid="arrival-lang-switcher"
            >
                {isAr ? "English" : "العربية"}
            </Link>
        </div>
    );
}

export default function ArrivalPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");

    const [lead, ...quads] = ARRIVAL_CARDS;

    return (
        <div
            className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="arrival-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            {/* First-screen composition. Full viewport height. No
                scrolling required to experience the arrival. */}
            <section className="relative w-full h-screen overflow-hidden">
                <ArrivalTopStrip />

                {/* Asymmetric editorial mosaic — a lead frame paired
                    with a 2×2 quadrant. The photograph carries the
                    meaning; the negative space between frames carries
                    the composure. */}
                <div className="pt-20 h-full w-full px-6 md:px-10 lg:px-16 pb-8 md:pb-10 lg:pb-12">
                    <div className="h-full grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4"
                        style={{ gridTemplateRows: "repeat(2, minmax(0, 1fr))" }}
                    >
                        {/* Lead frame */}
                        <div className="md:col-span-6 md:row-span-2 min-h-[220px] h-full">
                            <ArrivalCard card={lead} />
                        </div>

                        {/* 2×2 quadrant */}
                        {quads.map((card) => (
                            <div key={card.id} className="md:col-span-3 md:row-span-1 min-h-[160px] h-full">
                                <ArrivalCard card={card} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Provisional whisper — anchored bottom-centre, a
                    single line that exists only if the composition
                    reads too quietly on first review. Currently
                    hidden by default; can be enabled during review. */}
                {/* <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.28em] uppercase text-white/40">
                    Arrival — Prototype A
                </p> */}
            </section>
        </div>
    );
}
